import { useCallback, useEffect, useRef, useState } from "react";
import { ERROR_LOADING_TASK_HISTORY } from "constants/schedule-monitor.constants";
import TasksRepository from "repositories/tasks/tasks.repository";
import { ITaskRowViewModel } from "types/task";

interface IUseScheduleTasksResult {
    rows: ITaskRowViewModel[];
    loading: boolean;
    error: string | null;
    refetch: () => void;
}

/**
 * Fetches the task history for a schedule's detail modal: the 3 most recent tasks per status,
 * merged newest-first (ticket 02). Keyed on scheduleId so closing the modal (scheduleId becomes
 * null) or switching to a different row resets state immediately, rather than leaving the
 * previous row's tasks on screen while the next fetch is in flight.
 *
 * Deliberately does not re-run on the page's 60s auto-refresh: the fan-out is six requests per
 * schedule, so re-issuing it on every poll tick for a modal the user has open (rather than only
 * when they open it, switch rows, or explicitly refetch) would be a disproportionate request
 * volume for data that is already historical.
 */
const useScheduleTasks = (scheduleId: string | null): IUseScheduleTasksResult => {
    const [rows, setRows] = useState<ITaskRowViewModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refetchToken, setRefetchToken] = useState(0);

    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        abortControllerRef.current?.abort();

        if (!scheduleId) {
            setRows([]);
            setLoading(false);
            setError(null);
            return undefined;
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);
        setError(null);

        TasksRepository.getRecentTasksByStatus(scheduleId, controller.signal)
            .then((mergedRows) => {
                if (controller.signal.aborted) return;
                setRows(mergedRows);
                setLoading(false);
            })
            .catch(() => {
                if (controller.signal.aborted) return;
                setRows([]);
                setError(ERROR_LOADING_TASK_HISTORY);
                setLoading(false);
            });

        return () => {
            controller.abort();
        };
    }, [scheduleId, refetchToken]);

    const refetch = useCallback((): void => {
        setRefetchToken((token) => token + 1);
    }, []);

    return { rows, loading, error, refetch };
};

export default useScheduleTasks;
